import { GiftRebateSettlement } from '@shared/api/giftRebateSettlement/giftRebateSettlement.interface';
import { GiftSettlementStatus } from '@shared/api/giftSupplySettlement/giftSupplySettlement.interface';
import { phoneNumberStringFormator } from '@shared/utils/phoneNumberStringFormator';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';

import {
  computeRebatePayableFromLines,
  computeRebateTotals,
  floorToTenth,
  RebateRowCalc,
  RebateTotals,
  settlementLineToRebateRow,
  sortRebateRowsByDeliveredAt,
} from './rebate-table.utils';
import { statusLabel } from './rebate-settlement.utils';

// ─────────────────────────────────────────────
// 상조정산(리베이트) 상세내역 엑셀 다운로드
// 정산 완료 내역 상세 모달(RebateHistoryDetailModal)과 동일한
// 컬럼/계산식으로, 정산 라인 1건당 1행을 내보낸다.
//
// 프로젝트 표준(커뮤니티 xlsx + file-saver)을 따른다. 이 빌드는 셀 색/굵기/
// 테두리 같은 스타일을 지원하지 않으므로, 가시성은 라이브러리가 지원하는 범위
// — 넉넉한 행 높이·열 너비, 숫자 천단위 포맷, 헤더 고정, 자동 필터 — 로 확보한다.
// ─────────────────────────────────────────────

// 소수가 있는 값(수수료/수익금) 표기.
// 건별은 버리지 않고 그대로(부동소수 잡음만 제거되도록 6자리에서 반올림 표시),
// 합계 행만 floorToTenth 로 둘째 자리 버림한 값을 넣는다(화면 푸터와 동일).
const NUM_FMT = '#,##0.######';
const MONEY_LABELS = [
  '판매가(개당)',
  '원가(개당)',
  '결제금액',
  '상품원가',
  '수수료',
  '수익금',
  '총 수익금',
];

// 행 높이(pt) — 기본(약 15pt)은 글씨가 답답해 보여 넉넉히 키운다.
const HEADER_ROW_PT = 28;
const BODY_ROW_PT = 22;

const fmtD = (d?: Date | string): string => {
  if (!d) return '';
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate(),
  ).padStart(2, '0')}`;
};

const numOrEmpty = (v: number | null): number | string => (v != null ? v : '');

/**
 * 컬럼 폭 자동 조정 (한글 2.2칸/영문 1칸 기준).
 * 금액 셀은 천단위 콤마가 붙어 표시되므로 그만큼 폭을 더 잡아 글씨가 잘리지 않게 한다.
 * 최소 폭과 여유 패딩을 넉넉히 둬 셀이 답답해 보이지 않도록 한다.
 */
function autoWidth(ws: XLSX.WorkSheet) {
  const range = XLSX.utils.decode_range(ws['!ref'] ?? 'A1');
  const cols: XLSX.ColInfo[] = [];
  for (let C = range.s.c; C <= range.e.c; C++) {
    let max = 8; // 최소 폭
    for (let R = range.s.r; R <= range.e.r; R++) {
      const cell = ws[XLSX.utils.encode_cell({ r: R, c: C })];
      if (cell?.v == null) continue;
      let text = String(cell.v);
      // 숫자는 #,##0 으로 표시되므로 콤마 개수만큼 글자 폭을 보정한다.
      if (typeof cell.v === 'number') {
        const digits = Math.abs(Math.trunc(cell.v)).toString().length;
        text += ','.repeat(Math.max(0, Math.floor((digits - 1) / 3)));
      }
      let w = 0;
      for (const ch of text) w += ch.charCodeAt(0) > 0x7f ? 2.2 : 1;
      if (w > max) max = w;
    }
    cols.push({ wch: Math.min(Math.ceil(max) + 4, 60) });
  }
  ws['!cols'] = cols;
}

/** 헤더는 높게, 데이터 행은 넉넉히 — 글씨가 깨져 보이지 않도록 행 높이를 키운다. */
function setRowHeights(ws: XLSX.WorkSheet) {
  const range = XLSX.utils.decode_range(ws['!ref'] ?? 'A1');
  const rows: XLSX.RowInfo[] = [];
  for (let R = range.s.r; R <= range.e.r; R++) {
    rows[R] = { hpt: R === 0 ? HEADER_ROW_PT : BODY_ROW_PT };
  }
  ws['!rows'] = rows;
}

/** 헤더 라벨이 금액 컬럼이면 #,##0 숫자 포맷 적용 */
function applyMoneyFormat(ws: XLSX.WorkSheet) {
  const range = XLSX.utils.decode_range(ws['!ref'] ?? 'A1');
  const moneyCols: number[] = [];
  for (let C = range.s.c; C <= range.e.c; C++) {
    const hdr = ws[XLSX.utils.encode_cell({ r: 0, c: C })];
    if (hdr && MONEY_LABELS.includes(String(hdr.v))) moneyCols.push(C);
  }
  for (let R = range.s.r + 1; R <= range.e.r; R++) {
    for (const C of moneyCols) {
      const cell = ws[XLSX.utils.encode_cell({ r: R, c: C })];
      if (cell && typeof cell.v === 'number') cell.z = NUM_FMT;
    }
  }
}

/** 헤더 행(1행) 고정 — 스크롤해도 헤더가 항상 보이게 한다. */
function freezeHeader(ws: XLSX.WorkSheet) {
  ws['!freeze'] = { xSplit: 0, ySplit: 1 };
  if (!ws['!views']) ws['!views'] = [];
  (ws['!views'] as Array<Record<string, unknown>>)[0] = {
    state: 'frozen',
    ySplit: 1,
  };
}

/**
 * 헤더 행에 자동 필터(정렬/필터 드롭다운)를 건다.
 * 합계 행이 정렬/필터에 휩쓸리지 않도록 마지막 한 행은 범위에서 제외한다.
 */
function applyAutoFilter(ws: XLSX.WorkSheet) {
  const range = XLSX.utils.decode_range(ws['!ref'] ?? 'A1');
  if (range.e.r > range.s.r) range.e.r -= 1; // 합계 행 제외
  ws['!autofilter'] = { ref: XLSX.utils.encode_range(range) };
}

/** 폭/높이/숫자포맷/헤더고정/자동필터를 한 번에 적용해 시트를 마무리한다. */
function finishSheet(ws: XLSX.WorkSheet) {
  autoWidth(ws);
  setRowHeights(ws);
  applyMoneyFormat(ws);
  freezeHeader(ws);
  applyAutoFilter(ws);
}

/**
 * 계산된 행(RebateRowCalc[]) + 합계로 상조정산 상세 엑셀(단일 시트)을 다운로드한다.
 * 정산 완료 상세 모달·정산 예정·미정산 배송 내역이 동일 컬럼/계산식/포맷을 공유한다.
 *
 * @param calcRows  테이블 행 (화면에 보이는 행과 동일)
 * @param totals    합계 (화면 합계와 동일하게 합계 행에 표기)
 * @param fileName  저장 파일명 (.xlsx 포함)
 * @param sheetName 상세 시트명 (기본 '정산상세')
 */
export function exportRebateRowsExcel(
  calcRows: RebateRowCalc[],
  totals: RebateTotals,
  fileName: string,
  sheetName = '정산상세',
) {
  const wb = XLSX.utils.book_new();

  // 정산 라인(배송 1건)당 1행.
  // 화면 테이블과 동일한 컬럼: 판매가/원가(개당) → 수량 → 결제금액(판매가×수량)
  // → 상품원가(원가×수량) → 수수료(개당수수료×수량) → 수익금(라인 합계)
  const rows: Record<string, unknown>[] = calcRows.map((row, idx) => ({
    순번: idx + 1,
    상품: row.itemName,
    구매자: row.receiverName,
    배송완료일: fmtD(row.deliveredAt),
    배송요청일: fmtD(row.submittedAt),
    고인명: row.deceasedName ?? '',
    발인일: fmtD(row.coffinOutDate),
    '판매가(개당)': numOrEmpty(row.priceRetail),
    '원가(개당)': numOrEmpty(row.priceWhole),
    수량: row.quantity,
    결제금액: row.priceRetail != null ? row.priceRetail * row.quantity : '',
    상품원가: row.priceWhole != null ? row.priceWhole * row.quantity : '',
    수수료: row.fee != null ? row.fee * row.quantity : '',
    수익금: numOrEmpty(row.lineAmount),
    지도사: row.workerName ?? '',
    연락처: row.workerPhoneNumber
      ? phoneNumberStringFormator(row.workerPhoneNumber)
      : '',
  }));

  // 합계 행. 단가성(판매가/원가 개당) 컬럼은 행마다 개당 값이라 합산이 의미 없어 비우고,
  // 화면 푸터와 동일하게 수량·결제금액·상품원가·수수료·수익금 합계(소수점 포함)를 채운다.
  rows.push({
    순번: '',
    상품: '■ 합계',
    구매자: '',
    배송완료일: '',
    배송요청일: '',
    고인명: '',
    발인일: '',
    '판매가(개당)': '',
    '원가(개당)': '',
    수량: totals.totalQuantity,
    결제금액: totals.totalPriceRetail,
    상품원가: totals.totalPriceWhole,
    수수료: floorToTenth(totals.totalFeeRaw),
    수익금: floorToTenth(totals.totalLineAmount),
    지도사: '',
    연락처: '',
  });

  const ws = XLSX.utils.json_to_sheet(rows);
  finishSheet(ws);
  XLSX.utils.book_append_sheet(wb, ws, sheetName);

  const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  saveAs(new Blob([buf], { type: 'application/octet-stream' }), fileName);
}

/**
 * 상조정산 문서 1건의 상세내역(라인)을 엑셀로 다운로드한다.
 * (정산 완료 내역 상세 모달에서 사용)
 * @param s 상조정산 문서
 */
export function exportRebateSettlementDetailExcel(s: GiftRebateSettlement) {
  // 배송완료일 오름차순(가장 오래된 건이 1번) — 모달 화면과 동일 순서
  const calcRows = sortRebateRowsByDeliveredAt(s.lines.map(settlementLineToRebateRow));
  const totals = computeRebateTotals(calcRows);
  exportRebateRowsExcel(calcRows, totals, `상조정산상세_${s.year}년${s.month}월.xlsx`);
}

/**
 * 정산 완료 내역(연도별 캘린더)의 월별 요약 테이블을 엑셀로 다운로드한다.
 * 화면 표와 동일하게 정산월/상태/지급일/건수/총수익금을 월 오름차순으로 내보낸다.
 * (데이터가 있는 월만 포함하며, 월별 라인 상세는 상세보기 모달의 엑셀에서 받는다.)
 *
 * @param year        조회 연도
 * @param settlements 전체 정산 문서 (해당 연도만 필터링해 사용)
 */
export function exportRebateYearSummaryExcel(
  year: number,
  settlements: GiftRebateSettlement[],
) {
  // 완료(Paid)는 동결 금액, 예정(Draft)은 정산 예정 공식으로 재계산 (화면과 동일)
  const displayAmount = (s: GiftRebateSettlement): number =>
    s.status === GiftSettlementStatus.Paid
      ? s.totalAmount
      : computeRebatePayableFromLines(s.lines);

  const months = settlements
    .filter((s) => s.year === year)
    .sort((a, b) => a.month - b.month);

  const rows: Record<string, unknown>[] = months.map((s) => {
    // 지급일: 완료는 executedAt, 예정은 month+1(12월이면 다음해 1월) 15일
    const payout = s.executedAt
      ? new Date(s.executedAt as Date)
      : new Date(
          s.month === 12 ? s.year + 1 : s.year,
          s.month === 12 ? 0 : s.month,
          15,
        );
    return {
      정산월: `${s.year}년 ${s.month}월`,
      상태: statusLabel(s.status),
      지급구분: s.executedAt ? '완료' : '예정',
      지급일: fmtD(payout),
      건수: s.lines.length,
      '총 수익금': displayAmount(s),
    };
  });

  rows.push({
    정산월: '■ 합계',
    상태: '',
    지급구분: '',
    지급일: '',
    건수: months.reduce((sum, s) => sum + s.lines.length, 0),
    '총 수익금': months.reduce((sum, s) => sum + displayAmount(s), 0),
  });

  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet(rows);
  finishSheet(ws);
  XLSX.utils.book_append_sheet(wb, ws, `${year}년정산완료`);

  const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  saveAs(
    new Blob([buf], { type: 'application/octet-stream' }),
    `상조정산완료내역_${year}년.xlsx`,
  );
}
